import {
  type AreasInterest,
  type OnboardingStep,
  type PreferencesResponse,
  type UserPreference,
  type UserPreferenceLocation,
  type UserPreferenceMetadata,
} from '@/api/types/userPreferenceTypes';

import { parseHomeLocation } from './parseHomeLocation';

export function parseUserPreference(data: PreferencesResponse): UserPreference {
  const areas_interest = parseAreasOfInterest(
    data.results.find((result) => result.key.toLowerCase() === 'areas_interest')?.value,
  );

  const home_location = parseHomeLocation(
    data.results.find((result) => result.key.toLowerCase() === 'home_location')?.value,
  );

  const location = parseLocation(data.results.find((result) => result.key.toLowerCase() === 'location')?.value);

  const metadata = parseMetadata(data.metadata);

  const no_spoiler_enabled = Boolean(
    data.results.find((result) => result.key.toLowerCase() === 'no_spoiler_enabled')?.value,
  );

  const onboarding_steps_completed = parseOnboardingStepsCompleted(
    data.results.find((result) => result.key.toLowerCase() === 'onboarding_steps_completed')?.value,
  );

  const playback_options = parsePlaybackOptions(
    data.results.find((result) => result.key.toLowerCase() === 'playback_options')?.value,
  );

  const total_changes = Number(data.results.find((result) => result.key.toLowerCase() === 'total_changes')?.value) || 0;

  const braze_tune_in_fav = Boolean(data.results.find((r) => r.key.toLowerCase() === 'braze_tune_in_fav')?.value);

  const braze_enable_web_push_notification = Boolean(
    data.results.find((r) => r.key.toLowerCase() === 'braze_enable_web_push_notification')?.value,
  );

  const nextOnboardingStep = parseNextOnboardingStep({ onboarding_steps_completed, areas_interest });

  const userPreference: UserPreference = {
    areas_interest,
    home_location,
    location,
    metadata,
    no_spoiler_enabled,
    onboarding_steps_completed,
    playback_options,
    total_changes,
    braze_tune_in_fav,
    braze_enable_web_push_notification,
    parsedProps: {
      onboarding: {
        nextOnboardingStep,
      },
    },
  };

  return userPreference;
}

function parseAreasOfInterest(data: any): AreasInterest[] {
  if (Array.isArray(data)) {
    return data;
  }

  if (typeof data === 'string') {
    return [data as AreasInterest];
  }

  return [];
}

function parseOnboardingStepsCompleted(data: any): OnboardingStep[] {
  if (Array.isArray(data)) {
    return data;
  }

  if (typeof data === 'string') {
    return [data as OnboardingStep];
  }

  return [];
}

function parseLocation(data: any): UserPreferenceLocation {
  if (typeof data === 'string') {
    return { zipcode: data };
  }

  if (data && typeof data === 'object' && 'zipcode' in data) {
    return { zipcode: data.zipcode };
  }

  return { zipcode: '' };
}

function parseMetadata(data?: PreferencesResponse['metadata']): UserPreferenceMetadata | undefined {
  if (!data) {
    return;
  }

  if ('x-ent-home-location' in data && data['x-ent-home-location']) {
    return {
      'x-ent-home-location': data['x-ent-home-location'],
    };
  }
}

function parseNextOnboardingStep({
  onboarding_steps_completed,
  areas_interest,
}: {
  onboarding_steps_completed: OnboardingStep[];
  areas_interest: AreasInterest[];
}): OnboardingStep | null {
  const onboardingSteps: {
    number: number;
    step: OnboardingStep;
    isNext: boolean;
  }[] = [
    {
      number: 1,
      step: 'home_location',
      isNext: !onboarding_steps_completed.includes('home_location') || !onboarding_steps_completed,
    },
    {
      number: 2,
      step: 'areas_interest',
      isNext:
        onboarding_steps_completed.includes('home_location') && !onboarding_steps_completed.includes('areas_interest'),
    },
    {
      number: 3,
      step: 'favorite_leagues',
      isNext:
        onboarding_steps_completed.includes('home_location') &&
        onboarding_steps_completed.includes('areas_interest') &&
        !onboarding_steps_completed.includes('favorite_leagues') &&
        areas_interest.includes('sports'),
    },
  ];

  for (const onboardingStep of onboardingSteps) {
    if (onboardingStep.isNext) {
      // next step found, therefore onboarding has not been yet completed
      return onboardingStep.step;
    }
  }

  return null;
}

function parsePlaybackOptions(data: any): boolean {
  if (typeof data === 'boolean') {
    return data;
  }

  if (data === null || data === undefined) {
    // this means the value is missing/not been set yet and that this should default to true
    return true;
  }

  if (data === 'true') {
    return true;
  }

  if (data === 'false') {
    return false;
  }

  return Boolean(data);
}
